// ============================================================================
// NPSDConnectionPanel.tsx - Shared component for displaying connection panels
// Converted from Vue 2 Vue-NPSDConnectionPanel.ts
//
// Renders different columns based on connectionType:
// - person: Last Name, First Name, DOB, [Relationship], [Notes], [Role]
// - vehicle: Plate, State, Make, Model, Color, VIN
// - business: Name, Branch, Website, [Role]
// - client: Name, City, State, Website, [Role]
// ============================================================================
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';

import { getModuleType } from '@/utils/legacy-utils';
import moment from 'moment';

export type ConnectionType = 'person' | 'vehicle' | 'business' | 'client';

export interface RelationshipType {
  code: number | string;
  value: string;
}

export interface ConnectionRecord {
  id: string | number;
  // Person fields
  firstName?: string;
  lastName?: string;
  dob?: string;
  relationship?: number;
  notes?: string;
  roles?: string;
  // Vehicle fields
  plateNumber?: string;
  plateState?: string;
  year?: string | number;
  make?: string;
  model?: string;
  color?: string;
  vin?: string;
  // Business fields
  businessName?: string;
  branchName?: string;
  website?: string;
  // Client fields
  name?: string;
  locCity?: string;
  locState?: string;
  [key: string]: any;
}

export interface NPSDConnectionPanelProps {
  connections?: ConnectionRecord[];
  connectionType: ConnectionType;
  title: string;
  isEditMode?: boolean;
  showRelationship?: boolean;
  showRoles?: boolean;
  relationshipTypes?: RelationshipType[];
  disableLinks?: boolean;
  onAddConnection?: (connectionType: ConnectionType) => void;
  onViewItem?: (connectionType: ConnectionType, conn: ConnectionRecord) => void;
  onRemoveConnection?: (connectionType: ConnectionType, conn: ConnectionRecord) => void;
}

export function NPSDConnectionPanel({
  connections = [],
  connectionType,
  title,
  isEditMode = false,
  showRelationship = false,
  showRoles = false,
  relationshipTypes = [],
  disableLinks = false,
  onAddConnection,
  onViewItem,
  onRemoveConnection,
}: NPSDConnectionPanelProps) {
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = useState(false);

  // Computed: recordCount
  const recordCount = useMemo(() => connections?.length || 0, [connections]);

  // Mobile check (debounced to avoid excessive re-renders during resize)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const checkIfMobile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
    };
    setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // --- Format helpers ---

  const formatDate = useCallback((dateStr: string | undefined): string => {
    if (!dateStr) return '';
    if (typeof moment !== 'undefined') {
      return moment(dateStr).format('MM/DD/YYYY');
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  }, []);

  const getRelationshipLabel = useCallback(
    (code: number | undefined): string => {
      if (!code) return '';
      const rel = relationshipTypes.find(
        (r) => r.code === code || r.code === String(code)
      );
      return rel ? rel.value : '';
    },
    [relationshipTypes]
  );

  const getDisplayName = useCallback(
    (conn: ConnectionRecord): string => {
      switch (connectionType) {
        case 'person':
          return `${conn.firstName || ''} ${conn.lastName || ''}`.trim() || 'this person';
        case 'vehicle':
          return conn.plateNumber || 'this vehicle';
        case 'business':
          return conn.businessName || 'this business';
        case 'client':
          return conn.name || 'this client';
        default:
          return 'this item';
      }
    },
    [connectionType]
  );

  // --- Event handlers ---

  const handleAddConnection = useCallback(() => {
    onAddConnection?.(connectionType);
  }, [onAddConnection, connectionType]);

  const handleViewItem = useCallback(
    (conn: ConnectionRecord) => {
      if (disableLinks) return;
      onViewItem?.(connectionType, conn);
    },
    [disableLinks, onViewItem, connectionType]
  );

  const handleRemoveConnection = useCallback(
    (conn: ConnectionRecord) => {
      onRemoveConnection?.(connectionType, conn);
    },
    [onRemoveConnection, connectionType]
  );

  // --- Link URL generation ---

  const getItemLink = useCallback(
    (conn: ConnectionRecord): string => {
      if (disableLinks) return '#';
      const moduleType =
        typeof getModuleType === 'function'
          ? getModuleType().toUpperCase()
          : 'CAD';

      switch (connectionType) {
        case 'person':
          return `/?sectionName=${moduleType}&tab=Search&function=Open_Person&multiModule=true&params=${conn.id}`;
        case 'vehicle':
          return `/?sectionName=${moduleType}&tab=Search&function=Open_Vehicle&multiModule=true&params=${conn.id}`;
        case 'business':
          return `/?sectionName=${moduleType}&tab=Search&function=Open_Business&multiModule=true&params=${conn.id}`;
        case 'client':
          return `/?sectionName=${moduleType}&tab=Search&function=Open_ClientGroup&multiModule=true&params=${conn.id}`;
        default:
          return '#';
      }
    },
    [disableLinks, connectionType]
  );

  // --- Render helpers for clickable name cell ---

  const renderNameCell = useCallback(
    (conn: ConnectionRecord, displayText: string) => {
      if (!disableLinks) {
        return (
          <a
            href={getItemLink(conn)}
            className="item-link"
            onClick={(e) => {
              e.preventDefault();
              handleViewItem(conn);
            }}
          >
            {displayText}
          </a>
        );
      }
      return <span>{displayText}</span>;
    },
    [disableLinks, getItemLink, handleViewItem]
  );

  const renderMobileNameLink = useCallback(
    (conn: ConnectionRecord, displayText: string) => {
      if (!disableLinks) {
        return (
          <a
            href="#"
            className="card-title"
            onClick={(e) => {
              e.preventDefault();
              handleViewItem(conn);
            }}
          >
            {displayText}
          </a>
        );
      }
      return <span className="card-title">{displayText}</span>;
    },
    [disableLinks, handleViewItem]
  );

  // --- Desktop table renderers ---

  const renderPersonTable = () => {
    if (connections.length === 0) {
      return <div className="empty-cell">No person connections</div>;
    }
    return (
      <table>
        <thead>
          <tr>
            <th style={{ width: '22%' }}>Last Name</th>
            <th style={{ width: '18%' }}>First Name</th>
            <th style={{ width: '12%' }}>DOB</th>
            {showRelationship && <th style={{ width: '18%' }}>Relationship</th>}
            {showRoles && (
              <th style={{ width: '20%' }}>
                {showRelationship ? 'Notes' : 'Title/Role'}
              </th>
            )}
            {isEditMode && <th style={{ width: '10%' }}></th>}
          </tr>
        </thead>
        <tbody>
          {connections.map((conn) => (
            <tr key={`person-${conn.id}`}>
              <td>{renderNameCell(conn, conn.lastName || 'Unknown')}</td>
              <td>{conn.firstName || ''}</td>
              <td>{formatDate(conn.dob)}</td>
              {showRelationship && (
                <td>{getRelationshipLabel(conn.relationship)}</td>
              )}
              {showRoles && (
                <td>
                  {showRelationship ? conn.notes : conn.roles}
                </td>
              )}
              {isEditMode && (
                <td>
                  <button
                    type="button"
                    className="btn-delete"
                    onClick={() => handleRemoveConnection(conn)}
                  >
                    Remove
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderVehicleTable = () => {
    if (connections.length === 0) {
      return <div className="empty-cell">No vehicle connections</div>;
    }
    return (
      <table>
        <thead>
          <tr>
            <th style={{ width: '15%' }}>Plate</th>
            <th style={{ width: '8%' }}>State</th>
            <th style={{ width: '15%' }}>Make</th>
            <th style={{ width: '15%' }}>Model</th>
            <th style={{ width: '12%' }}>Color</th>
            <th style={{ width: '25%' }}>VIN</th>
            {isEditMode && <th style={{ width: '10%' }}></th>}
          </tr>
        </thead>
        <tbody>
          {connections.map((conn) => (
            <tr key={`vehicle-${conn.id}`}>
              <td>{renderNameCell(conn, conn.plateNumber || 'Unknown')}</td>
              <td>{conn.plateState || ''}</td>
              <td>{conn.make || ''}</td>
              <td>{conn.model || ''}</td>
              <td>{conn.color || ''}</td>
              <td>{conn.vin || ''}</td>
              {isEditMode && (
                <td>
                  <button
                    type="button"
                    className="btn-delete"
                    onClick={() => handleRemoveConnection(conn)}
                  >
                    Remove
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderBusinessTable = () => {
    if (connections.length === 0) {
      return <div className="empty-cell">No business connections</div>;
    }
    return (
      <table>
        <thead>
          <tr>
            <th style={{ width: '30%' }}>Name</th>
            <th style={{ width: '25%' }}>Branch</th>
            <th style={{ width: '25%' }}>Website</th>
            {showRoles && <th style={{ width: '20%' }}>Title/Role</th>}
            {isEditMode && <th style={{ width: '10%' }}></th>}
          </tr>
        </thead>
        <tbody>
          {connections.map((conn) => (
            <tr key={`business-${conn.id}`}>
              <td>{renderNameCell(conn, conn.businessName || 'Unknown')}</td>
              <td>{conn.branchName || ''}</td>
              <td>{conn.website || ''}</td>
              {showRoles && <td>{conn.roles || ''}</td>}
              {isEditMode && (
                <td>
                  <button
                    type="button"
                    className="btn-delete"
                    onClick={() => handleRemoveConnection(conn)}
                  >
                    Remove
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderClientTable = () => {
    if (connections.length === 0) {
      return <div className="empty-cell">No client connections</div>;
    }
    return (
      <table>
        <thead>
          <tr>
            <th style={{ width: '28%' }}>Name</th>
            <th style={{ width: '18%' }}>City</th>
            <th style={{ width: '12%' }}>State</th>
            <th style={{ width: '22%' }}>Website</th>
            {showRoles && <th style={{ width: '20%' }}>Title/Role</th>}
            {isEditMode && <th style={{ width: '10%' }}></th>}
          </tr>
        </thead>
        <tbody>
          {connections.map((conn) => (
            <tr key={`client-${conn.id}`}>
              <td>{renderNameCell(conn, conn.name || 'Unknown')}</td>
              <td>{conn.locCity || ''}</td>
              <td>{conn.locState || ''}</td>
              <td>{conn.website || ''}</td>
              {showRoles && <td>{conn.roles || ''}</td>}
              {isEditMode && (
                <td>
                  <button
                    type="button"
                    className="btn-delete"
                    onClick={() => handleRemoveConnection(conn)}
                  >
                    Remove
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // --- Desktop table dispatch ---

  const renderDesktopTable = () => {
    switch (connectionType) {
      case 'person':
        return renderPersonTable();
      case 'vehicle':
        return renderVehicleTable();
      case 'business':
        return renderBusinessTable();
      case 'client':
        return renderClientTable();
      default:
        return null;
    }
  };

  // --- Mobile card renderers ---

  const renderPersonMobileCards = () => {
    if (connections.length === 0) {
      return <div className="empty-state">No person connections</div>;
    }
    return connections.map((conn) => (
      <div key={`person-mobile-${conn.id}`} className="result-card">
        <div className="card-header">
          {renderMobileNameLink(conn, conn.lastName || 'Unknown')}
          {isEditMode && (
            <button
              type="button"
              className="btn-delete"
              onClick={() => handleRemoveConnection(conn)}
            >
              Remove
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="card-row">
            <span className="card-label">First Name</span>
            <span className="card-value">{conn.firstName || '\u2014'}</span>
          </div>
          <div className="card-row">
            <span className="card-label">DOB</span>
            <span className="card-value">{formatDate(conn.dob) || '\u2014'}</span>
          </div>
          {showRelationship && (
            <div className="card-row">
              <span className="card-label">Relationship</span>
              <span className="card-value">
                {getRelationshipLabel(conn.relationship) || '\u2014'}
              </span>
            </div>
          )}
          {showRoles && (
            <div className="card-row full-width">
              <span className="card-label">
                {showRelationship ? 'Notes' : 'Title/Role'}
              </span>
              <span className="card-value">
                {(showRelationship ? conn.notes : conn.roles) || '\u2014'}
              </span>
            </div>
          )}
        </div>
      </div>
    ));
  };

  const renderVehicleMobileCards = () => {
    if (connections.length === 0) {
      return <div className="empty-state">No vehicle connections</div>;
    }
    return connections.map((conn) => (
      <div key={`vehicle-mobile-${conn.id}`} className="result-card">
        <div className="card-header">
          {renderMobileNameLink(conn, conn.plateNumber || 'Unknown')}
          {isEditMode && (
            <button
              type="button"
              className="btn-delete"
              onClick={() => handleRemoveConnection(conn)}
            >
              Remove
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="card-row">
            <span className="card-label">State</span>
            <span className="card-value">{conn.plateState || '\u2014'}</span>
          </div>
          <div className="card-row">
            <span className="card-label">Make</span>
            <span className="card-value">{conn.make || '\u2014'}</span>
          </div>
          <div className="card-row">
            <span className="card-label">Model</span>
            <span className="card-value">{conn.model || '\u2014'}</span>
          </div>
          <div className="card-row">
            <span className="card-label">Color</span>
            <span className="card-value">{conn.color || '\u2014'}</span>
          </div>
          <div className="card-row full-width">
            <span className="card-label">VIN</span>
            <span className="card-value">{conn.vin || '\u2014'}</span>
          </div>
        </div>
      </div>
    ));
  };

  const renderBusinessMobileCards = () => {
    if (connections.length === 0) {
      return <div className="empty-state">No business connections</div>;
    }
    return connections.map((conn) => (
      <div key={`business-mobile-${conn.id}`} className="result-card">
        <div className="card-header">
          {renderMobileNameLink(conn, conn.businessName || 'Unknown')}
          {isEditMode && (
            <button
              type="button"
              className="btn-delete"
              onClick={() => handleRemoveConnection(conn)}
            >
              Remove
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="card-row">
            <span className="card-label">Branch</span>
            <span className="card-value">{conn.branchName || '\u2014'}</span>
          </div>
          <div className="card-row">
            <span className="card-label">Website</span>
            <span className="card-value">{conn.website || '\u2014'}</span>
          </div>
          {showRoles && (
            <div className="card-row full-width">
              <span className="card-label">Title/Role</span>
              <span className="card-value">{conn.roles || '\u2014'}</span>
            </div>
          )}
        </div>
      </div>
    ));
  };

  const renderClientMobileCards = () => {
    if (connections.length === 0) {
      return <div className="empty-state">No client connections</div>;
    }
    return connections.map((conn) => (
      <div key={`client-mobile-${conn.id}`} className="result-card">
        <div className="card-header">
          {renderMobileNameLink(conn, conn.name || 'Unknown')}
          {isEditMode && (
            <button
              type="button"
              className="btn-delete"
              onClick={() => handleRemoveConnection(conn)}
            >
              Remove
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="card-row">
            <span className="card-label">City</span>
            <span className="card-value">{conn.locCity || '\u2014'}</span>
          </div>
          <div className="card-row">
            <span className="card-label">State</span>
            <span className="card-value">{conn.locState || '\u2014'}</span>
          </div>
          <div className="card-row">
            <span className="card-label">Website</span>
            <span className="card-value">{conn.website || '\u2014'}</span>
          </div>
          {showRoles && (
            <div className="card-row full-width">
              <span className="card-label">Title/Role</span>
              <span className="card-value">{conn.roles || '\u2014'}</span>
            </div>
          )}
        </div>
      </div>
    ));
  };

  // --- Mobile cards dispatch ---

  const renderMobileCards = () => {
    switch (connectionType) {
      case 'person':
        return renderPersonMobileCards();
      case 'vehicle':
        return renderVehicleMobileCards();
      case 'business':
        return renderBusinessMobileCards();
      case 'client':
        return renderClientMobileCards();
      default:
        return null;
    }
  };

  return (
    <div className={`panel${theme ? ' ' + theme : ''}`}>
      <div className="panel-header">
        <span>{title}</span>
        {isEditMode && (
          <button
            type="button"
            className="btn-add"
            onClick={handleAddConnection}
          >
            <i className="fas fa-plus"></i> Connection
          </button>
        )}
      </div>
      <div className="panel-body">
        {/* Desktop Table */}
        <div className="table-container">
          {renderDesktopTable()}
        </div>

        {/* Mobile Cards */}
        <div className="mobile-cards">
          {renderMobileCards()}
        </div>
      </div>
    </div>
  );
}

(window as any).NPSDConnectionPanelReact = NPSDConnectionPanel;
